import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiFailure, type Doctor, type TestMasterRow } from './api'

/* The desk's one-step bill (POST /api/invoices/quick), shared by the desktop
 * home page and the phone screen: who it is for, what is billed, the total,
 * and taking the money. Prices come from the rate cards; the desk can change
 * one, and the server records that it did. */

export type QbGender = '' | 'male' | 'female' | 'other'
export type QbMode = 'cash' | 'upi' | 'card'
export type QbBlocker = 'name' | 'lines' | 'price' | 'desc' | 'cash'

export const QB_BLOCKER_EN: Record<QbBlocker, string> = {
  name: 'Enter the patient’s name.',
  lines: 'Add a consultation or a test.',
  price: 'Every line needs a price.',
  desc: 'Describe the extra charge.',
  cash: 'Cash received is less than the bill.',
}

export interface QbPatient { id: string; fullName: string; patientCode: string; mobile: string | null; ageYears?: number | null; gender?: string | null }

export interface QbLine {
  key: string
  kind: 'consultation' | 'lab_test' | 'other'
  refId: string | null
  label: string
  /** The rate-card price, or what the desk typed over it. */
  price: string
  ratePrice: string
}

export interface QbResult {
  invoice: { id: string; invoiceNo: string; total: string; createdAt: string; status: string }
  patient: QbPatient
  items: { id: string; description: string; quantity: number; unitPrice: string; lineTotal: string; kind: string | null }[]
  totals: { paid: string; outstanding: string; settlement: string }
  receipt: { receiptNo: string; mode: string; amountPaid: string; cashReceived: string | null; changeGiven: string | null; collectedAt: string } | null
  labBookingIds: string[]
}

let seq = 0
const key = () => `l${++seq}`

export function rupees(v: string | number | null | undefined): string {
  if (v == null || v === '') return '₹0'
  const n = Number(v)
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`
}

export function useQuickBill() {
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [tests, setTests] = useState<TestMasterRow[]>([])
  const [loadError, setLoadError] = useState('')

  const [existing, setExisting] = useState<QbPatient | null>(null)
  const [name, setName] = useState('')
  const [gender, setGender] = useState<QbGender>('')
  const [age, setAge] = useState('')
  const [lines, setLines] = useState<QbLine[]>([])
  const [mode, setMode] = useState<QbMode>('cash')
  const [cash, setCash] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<QbResult | null>(null)

  useEffect(() => {
    void Promise.all([
      api.get<{ doctors: Doctor[] }>('/api/doctors'),
      api.get<{ tests: TestMasterRow[] }>('/api/tests?limit=500'),
    ]).then(([d, t]) => {
      setDoctors(d.doctors.filter((x) => x.isActive))
      // /api/tests lists active tests only unless asked for retired ones.
      setTests(t.tests)
    }).catch((e: Error) => setLoadError(e.message))
  }, [])

  const total = useMemo(() => lines.reduce((s, l) => s + (Number(l.price) || 0), 0), [lines])
  const change = mode === 'cash' && cash !== '' ? Number(cash) - total : null

  const addDoctor = useCallback((d: Doctor) => setLines((ls) => [...ls, {
    key: key(), kind: 'consultation', refId: d.id, label: `Consultation · ${d.fullName}`,
    price: String(Number(d.consultationFee ?? 0)), ratePrice: String(Number(d.consultationFee ?? 0)),
  }]), [])
  const addTest = useCallback((t: TestMasterRow) => setLines((ls) => ls.some((l) => l.refId === t.id) ? ls : [...ls, {
    key: key(), kind: 'lab_test', refId: t.id, label: t.name,
    price: t.typicalCost == null ? '' : String(Number(t.typicalCost)), ratePrice: t.typicalCost == null ? '' : String(Number(t.typicalCost)),
  }]), [])
  const addOther = useCallback(() => setLines((ls) => [...ls, { key: key(), kind: 'other', refId: null, label: '', price: '', ratePrice: '' }]), [])
  const updateLine = useCallback((k: string, patch: Partial<QbLine>) => setLines((ls) => ls.map((l) => (l.key === k ? { ...l, ...patch } : l))), [])
  const removeLine = useCallback((k: string) => setLines((ls) => ls.filter((l) => l.key !== k)), [])

  const reset = useCallback(() => {
    setExisting(null); setName(''); setGender(''); setAge(''); setLines([]); setMode('cash'); setCash(''); setReference('')
    setError(''); setResult(null)
  }, [])

  /** Why the bill cannot go yet, as a code each screen words itself; null when it can. */
  const blocker: QbBlocker | null = !existing && name.trim().length < 2 ? 'name'
    : lines.length === 0 ? 'lines'
    : lines.some((l) => l.price === '' || Number.isNaN(Number(l.price)) || Number(l.price) < 0) ? 'price'
    : lines.some((l) => l.kind === 'other' && !l.label.trim()) ? 'desc'
    : mode === 'cash' && cash !== '' && Number(cash) < total ? 'cash'
    : null

  const submit = useCallback(async (collect: boolean) => {
    if (blocker) return
    setBusy(true); setError('')
    try {
      const r = await api.post<QbResult>('/api/invoices/quick', {
        ...(existing ? { patientId: existing.id } : {
          patient: { fullName: name.trim(), gender: gender || null, ageYears: age === '' ? null : Number(age) },
        }),
        items: lines.map((l) => (l.kind === 'other'
          ? { kind: 'other', description: l.label.trim(), unitPrice: l.price }
          : l.kind === 'consultation'
            ? { kind: 'consultation', doctorId: l.refId, unitPrice: l.price !== l.ratePrice ? l.price : null }
            : { kind: 'lab_test', testId: l.refId, unitPrice: l.price !== l.ratePrice ? l.price : null })),
        payment: collect ? { mode, referenceNo: reference.trim() || null, cashReceived: mode === 'cash' && cash !== '' ? cash : null } : null,
      })
      setResult(r)
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Could not save the bill.')
    } finally {
      setBusy(false)
    }
  }, [blocker, existing, name, gender, age, lines, mode, reference, cash])

  return {
    doctors, tests, loadError,
    existing, setExisting, name, setName, gender, setGender, age, setAge,
    lines, addDoctor, addTest, addOther, updateLine, removeLine,
    mode, setMode, cash, setCash, reference, setReference,
    total, change, blocker, busy, error, setError, submit, result, reset,
  }
}

/** Patients matching what the desk typed, for "bill an existing patient". */
export function usePatientMatches(term: string, enabled: boolean) {
  const [matches, setMatches] = useState<QbPatient[]>([])
  useEffect(() => {
    const q = term.trim()
    if (!enabled || q.length < 3) { setMatches([]); return }
    const t = setTimeout(() => {
      void api.get<{ patients: QbPatient[] }>(`/api/patients/search?q=${encodeURIComponent(q)}`)
        .then((r) => setMatches(r.patients.slice(0, 5))).catch(() => setMatches([]))
    }, 220)
    return () => clearTimeout(t)
  }, [term, enabled])
  return matches
}
